
import { useState, useCallback } from 'react';
import { nuiteeBookingService } from '@/services/nuiteeBookingService';
import { debug } from '@/utils/logger';
import { openNuiteeSsoInNewTab } from '@/lib/booking/openNuiteeSsoInNewTab';
import {
  bookingErrorCopy,
  MISSING_PLACE_ERROR,
  type BookingErrorCopy,
} from '@/lib/booking/bookingErrors';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_BOOKING_SSO_TAB_OPENED,
  EVENT_BOOKING_SSO_POPUP_BLOCKED,
  EVENT_BOOKING_SSO_API_ERROR,
} from '@/lib/analyticsEvents';

/** Search params for whitelabel deep link (dates/guests). */
export interface WhitelabelForPlaceSearchParams {
  checkIn?: string;
  checkOut?: string;
  adults?: number;
  rooms?: number;
}

export interface OpenWhitelabelForPlaceParams {
  placeId: string;
  /**
   * The hotel's Nuitee id when the place already has one (hotels from booking search).
   * Skips the place lookup, which returns the first hotel within 1 km of the place and can
   * open a neighbouring hotel.
   */
  hotelId?: string;
  searchParams?: WhitelabelForPlaceSearchParams;
  postId?: string;
  googlePlaceId?: string;
}

/**
 * Shared hook for opening the Nuitee whitelabel for a specific hotel with dates/guests (deep link).
 * Used when non-admin clicks Book (opens whitelabel in new tab) and when user selects "Whitelabel"
 * in BookingMethodSelectionModal.
 *
 * Flow: getHotelIdFromPlaceId → buildWhitelabelDeepLinkPath → getSSOLink → navigate tab.
 *
 * Uses the tab-first pattern: opens a placeholder tab synchronously on the user-gesture
 * stack, then navigates it to the SSO URL once the API chain completes. This prevents
 * popup blockers from silently swallowing the open (the two-step async chain is especially
 * vulnerable: hotel-id lookup + getSSOLink both happen before the tab would otherwise open).
 */
export function useOpenWhitelabelForPlace(): {
  openWhitelabelForPlace: (params: OpenWhitelabelForPlaceParams) => void;
  isOpening: boolean;
  blockedLoginUrl: string | undefined;
  clearBlockedState: () => void;
  /** Set when the booking can't be opened; callers show it in BookingErrorModal. */
  bookingError: BookingErrorCopy | null;
  clearBookingError: () => void;
} {
  const [isOpening, setIsOpening] = useState(false);
  /** Set when the popup was blocked; used to drive the BlockedModal. */
  const [blockedLoginUrl, setBlockedLoginUrl] = useState<string | undefined>(undefined);
  const [bookingError, setBookingError] = useState<BookingErrorCopy | null>(null);

  const openWhitelabelForPlace = useCallback((params: OpenWhitelabelForPlaceParams) => {
    const { placeId, hotelId: knownHotelId, searchParams, postId, googlePlaceId } = params;

    if (!placeId?.trim() && !knownHotelId?.trim()) {
      setBookingError(MISSING_PLACE_ERROR);
      return;
    }

    setIsOpening(true);
    setBlockedLoginUrl(undefined);
    setBookingError(null);

    openNuiteeSsoInNewTab({
      surface: 'place_card',
      fetchLoginUrl: async () => {
        let hotelId = knownHotelId?.trim();
        if (!hotelId) {
          debug('[useOpenWhitelabelForPlace] Looking up hotel ID for placeId', { placeId });
          const response = await nuiteeBookingService.getHotelIdFromPlaceId(placeId);

          if (!response.data?.hotelId) {
            const err = new Error('Hotel not in inventory') as Error & { code: string };
            err.code = 'HOTEL_NOT_IN_INVENTORY';
            throw err;
          }
          hotelId = response.data.hotelId as string;
        }
        const adults = searchParams?.adults ?? 2;
        const rooms = searchParams?.rooms ?? 1;

        debug('[useOpenWhitelabelForPlace] Building whitelabel deep link', {
          hotelId,
          checkIn: searchParams?.checkIn,
          checkOut: searchParams?.checkOut,
          adults,
          rooms,
          postId: postId || null,
          googlePlaceId: googlePlaceId ?? placeId,
        });

        const deepLinkPath = nuiteeBookingService.buildWhitelabelDeepLinkPath({
          hotelId,
          checkIn: searchParams?.checkIn,
          checkOut: searchParams?.checkOut,
          adults,
          rooms,
          postId,
          googlePlaceId: googlePlaceId ?? placeId,
        });

        const ssoResponse = await nuiteeBookingService.getSSOLink(deepLinkPath);

        if (!ssoResponse.data?.loginUrl) {
          throw new Error('Missing loginUrl in SSO response');
        }

        debug('[useOpenWhitelabelForPlace] SSO link obtained', { hotelId });
        return ssoResponse.data.loginUrl;
      },
      onSuccess: (loginUrl) => {
        WebAnalytics.track(EVENT_BOOKING_SSO_TAB_OPENED, {
          surface: 'place_card',
          loginUrl: !!loginUrl,
          ...WebAnalytics.getReferringCreatorProps(),
        });
        setIsOpening(false);
      },
      onBlocked: (loginUrl) => {
        WebAnalytics.track(EVENT_BOOKING_SSO_POPUP_BLOCKED, { surface: 'place_card', hasLoginUrl: !!loginUrl });
        setBlockedLoginUrl(loginUrl);
        setIsOpening(false);
      },
      onApiError: (errorCode, errorStatus) => {
        WebAnalytics.track(EVENT_BOOKING_SSO_API_ERROR, { surface: 'place_card', errorCode, errorStatus });
        setBookingError(bookingErrorCopy(errorCode, errorStatus));
        setIsOpening(false);
      },
    });
  }, []);

  const clearBlockedState = useCallback(() => {
    setBlockedLoginUrl(undefined);
  }, []);

  const clearBookingError = useCallback(() => {
    setBookingError(null);
  }, []);

  return {
    openWhitelabelForPlace,
    isOpening,
    blockedLoginUrl,
    clearBlockedState,
    bookingError,
    clearBookingError,
  };
}
